import type { History } from 'history'
import type { MessageDescriptor, PrimitiveType } from 'react-intl'

import type { ManagedAccessToken } from '@b-stock/fe-core/bstock-react'

import type { CheckEmailFulfilledPayload } from './reducers/checkEmail'
import type { SubmitEmailFulfilledPayload } from './reducers/submitEmail'
import types from './types'

/**
 * The string-returning half of react-intl's `formatMessage`. The sagas only ever
 * feed the result to `showSnackbar`, so narrowing to this one overload keeps
 * `useIntl().formatMessage` assignable while also accepting the pre-bound
 * closures some call sites pass (which can't satisfy the `ReactNode[]` overload).
 */
type FormatMessage = (
  descriptor: MessageDescriptor,
  values?: Record<string, PrimitiveType>
) => string

export type CheckEmailActionPayload = {
  email: string
}
export const checkEmailPending = () => ({
  type: types.CHECK_EMAIL_PENDING,
})
export const checkEmailFulfilled = (payload: CheckEmailFulfilledPayload) => ({
  type: types.CHECK_EMAIL_FULFILLED,
  payload,
})
// The raw caught error, forwarded verbatim — see `accessTokenRejected` in
// ../login/actions for why this stays opaque rather than `AsyncReducerError`.
export const checkEmailRejected = (error: unknown) => ({
  type: types.CHECK_EMAIL_REJECTED,
  error,
})

export const setSignUpEmail = (signUpEmail: string) => ({
  type: types.SET_SIGN_UP_EMAIL,
  signUpEmail,
})

export type FetchVerificationCodeActionPayload = {
  id: string
  email?: string
  formatMessage?: FormatMessage
  exists?: boolean
  hasCredentials?: boolean
  isEmailVerified?: boolean
}
export const fetchVerificationCodeAction = (
  payload: FetchVerificationCodeActionPayload
) => ({
  type: types.FETCH_VERIFICATION_CODE,
  payload,
})
export const fetchVerificationCodePending = () => ({
  type: types.FETCH_VERIFICATION_CODE_PENDING,
})
export const fetchVerificationCodeFulfilled = () => ({
  type: types.FETCH_VERIFICATION_CODE_FULFILLED,
})
/**
 * Unlike the other `*Rejected` creators this one is handed a shape the saga
 * assembles from the caught error, not the error itself.
 */
type FetchVerificationCodeRejectedError = {
  status?: number
  message?: string
  remainingSeconds?: number
}
export const fetchVerificationCodeRejected = (
  error: FetchVerificationCodeRejectedError
) => ({
  type: types.FETCH_VERIFICATION_CODE_REJECTED,
  error,
})

export type SubmitEmailActionPayload = {
  email: string
  id?: string
  formatMessage?: FormatMessage
}
export const submitEmailAction = (payload: SubmitEmailActionPayload) => ({
  type: types.SUBMIT_EMAIL,
  payload,
})
export const submitEmailPending = () => ({
  type: types.SUBMIT_EMAIL_PENDING,
})
export const submitEmailFulfilled = (payload: SubmitEmailFulfilledPayload) => ({
  type: types.SUBMIT_EMAIL_FULFILLED,
  payload,
})
// The raw caught error, forwarded verbatim — see `accessTokenRejected` in
// ../login/actions for why this stays opaque rather than `AsyncReducerError`.
export const submitEmailRejected = (error: unknown) => ({
  type: types.SUBMIT_EMAIL_REJECTED,
  error,
})

/**
 * Assembled from the sign-up wizard's accumulated `SignUpValues`, every field of
 * which is optional: the steps fill them in progressively, and the URL-derived
 * ones (`siteAbb`, `subflow`, `redirectAfterSignup`) are absent on a plain
 * `/signup`. The fields below were previously declared required, which only held
 * because the call site passed `any`. `setPasswordSaga` already guards each one
 * (`if (!brid)`, `id || jwtData.personId`, …), so this states what has always
 * been dispatched rather than changing it.
 */
export type SetPasswordActionPayload = {
  email?: string
  verificationCode?: string
  acceptMarketingSolicitation?: boolean
  /** Transactional SMS opt-in from onboarding (create-account `subscribe` checkbox) */
  subscribe?: boolean
  country?: string
  brid?: string
  firstName?: string
  lastName?: string
  phone?: string
  password?: string
  id?: string
  redirectAfterSignup?: string
  siteAbb?: string
  subflow?: string
}
export const setPasswordAction = (
  payload: SetPasswordActionPayload,
  history: History,
  managedAccessToken: ManagedAccessToken
) => ({
  type: types.SET_PASSWORD,
  payload,
  history,
  managedAccessToken,
})
export const setPasswordPending = () => ({
  type: types.SET_PASSWORD_PENDING,
})
export const setPasswordFulfilled = () => ({
  type: types.SET_PASSWORD_FULFILLED,
})
// The raw caught error, forwarded verbatim — see `accessTokenRejected` in
// ../login/actions for why this stays opaque rather than `AsyncReducerError`.
export const setPasswordRejected = (error: unknown) => ({
  type: types.SET_PASSWORD_REJECTED,
  error,
})
